"use client";

import { useState, useEffect } from "react";

export default function SurveyTable() {
  const [surveys, setSurveys] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [selectedConference, setSelectedConference] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConferences, setLoadingConferences] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const BASE_SURVEY_URL =
    "https://eventscons.com/eventbackendnew/api/admin/surveys/conference";
  const CONFERENCES_URL =
    "https://eventscons.com/eventbackendnew/api/conferences/all";

  const getAuthToken = () => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token")
      );
    }
    return null;
  };

  // Conferences fetch
  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      const token = getAuthToken();
      const res = await fetch(CONFERENCES_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to load conferences: ${res.status}`);
      }

      const json = await res.json();
      // assume structure { data: [...] }
      const list = json.data || [];
      setConferences(list);

      // default select first conference (optional)
      if (list.length > 0 && !selectedConference) {
        setSelectedConference(list[0].id);
      }
    } catch (err) {
      console.error("Error fetching conferences:", err);
    } finally {
      setLoadingConferences(false);
    }
  };

  // Surveys fetch (depends on selectedConference)
  const fetchSurveys = async () => {
    if (!selectedConference) {
      setSurveys([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login first.");
      }

      const API_URL = `${BASE_SURVEY_URL}/${selectedConference}`;

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Server error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      const surveyData = data.data || [];

      const processedSurveys = surveyData.map((survey) => ({
        id: survey.id,
        speaker_id: survey.speaker_id,
        created_at: survey.created_at,
        userName: survey.speaker?.user?.name || "N/A",
        userEmail: survey.speaker?.user?.email || "N/A",
        answers: {
          q1_satisfaction: survey.q1_satisfaction,
          q2_clarity: survey.q2_clarity,
          q3_technical_support: survey.q3_technical_support,
          q4_organization: survey.q4_organization,
          q5_staff_support: survey.q5_staff_support,
          q6_schedule_management: survey.q6_schedule_management,
          q7_like_experience: survey.q7_like_experience,
          q8_improvement: survey.q8_improvement,
          q9_future_participation: survey.q9_future_participation,
          q10_final_comments: survey.q10_final_comments,
        },
      }));

      setSurveys(processedSurveys);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (survey) => {
    setSelectedSurvey(survey);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedSurvey(null);
  };

  // Initial: load conferences
  useEffect(() => {
    fetchConferences();
  }, []);

  // Jab conference change ho, surveys reload
  useEffect(() => {
    if (selectedConference) {
      fetchSurveys();
    }
  }, [selectedConference]);

  // Questions text
  const questions = {
    q1_satisfaction:
      "1. How satisfied were you with your overall experience as a speaker?",
    q2_clarity:
      "2. How clear were the instructions and communication you received before the event?",
    q3_technical_support:
      "3. How would you rate the technical support provided (AV, slides, stage, timing)?",
    q4_organization: "4. How organized was the event overall?",
    q5_staff_support:
      "5. How would you rate the staff support during the event?",
    q6_schedule_management:
      "6. Did you feel your session was well scheduled and managed?",
    q7_like_experience:
      "7. What did you like about your experience as a speaker?",
    q8_improvement:
      "8. What challenges did you face or what could be improved?",
    q9_future_participation:
      "9. Would you participate as a speaker again in future events?",
    q10_final_comments: "10. Any final comments or suggestions?",
  };

  // Styles
  const containerStyle = {
    padding: "24px",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  };

  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    padding: "16px 20px",
    textAlign: "left",
    fontWeight: "600",
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    backgroundColor: "#f1f5f9",
    borderBottom: "2px solid #e2e8f0",
  };

  const tdStyle = {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
  };

  const btnStyle = {
    padding: "8px 16px",
    background: "linear-gradient(135deg, #9B1321 0%, #9B1321 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontWeight: "500",
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
  };

  const btnHoverStyle = {
    background: "linear-gradient(135deg, #9B1321 0%, #9B1321 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(59,130,246,0.4)",
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 9999,
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "20px",
    maxWidth: "1000px",
    maxHeight: "90vh",
    width: "100%",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
  };

  if (loading && !loadingConferences) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #9B1321",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "20px",
            }}
          ></div>
          <span style={{ fontSize: "18px", color: "#64748b" }}>
            Loading surveys...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "12px",
            padding: "32px",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#dc2626",
              marginBottom: "12px",
            }}
          >
            Error Loading Data
          </h2>
          <p style={{ color: "#b91c1c", marginBottom: "20px" }}>{error}</p>
          <button
            onClick={fetchSurveys}
            style={{
              ...btnStyle,
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              padding: "12px 24px",
            }}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        tr:hover {
          background-color: #f8fafc !important;
        }
        button:hover {
          transform: translateY(-1px) !important;
        }
      `}</style>

      {/* Header + Conference dropdown */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "32px",
              fontWeight: "800",
              background:
                "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0,
            }}
          >
            Survey Responses ({surveys.length})
          </h2>
          {/* <p style={{ margin: "8px 0 0 0", color: "#64748b", fontSize: "14px" }}>
            {selectedConference
              ? `Conference ID: ${selectedConference}`
              : "Please select a conference to view surveys"}
          </p> */}
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <select
            value={selectedConference}
            onChange={(e) => setSelectedConference(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #cbd5f5",
              minWidth: "220px",
              fontSize: "14px",
              outline: "none",
            }}
          >
            <option value="">
              {loadingConferences
                ? "Loading conferences..."
                : "Select Conference"}
            </option>
            {conferences.map((conf) => (
              <option key={conf.id} value={conf.id}>
                {conf.title || conf.name || `Conference ${conf.id}`}
              </option>
            ))}
          </select>

          <button
            onClick={fetchSurveys}
            style={btnStyle}
            disabled={!selectedConference}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {surveys.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 40px",
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "20px",
              opacity: 0.3,
            }}
          >
            📭
          </div>
          <p
            style={{
              fontSize: "20px",
              color: "#64748b",
              marginBottom: "24px",
            }}
          >
            {selectedConference
              ? "No survey responses found for this conference"
              : "Please select a conference to view survey responses"}
          </p>
          {selectedConference && (
            <button onClick={fetchSurveys} style={btnStyle}>
              Reload Data
            </button>
          )}
        </div>
      ) : (
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>🆔 Speaker/Attendance ID</th>
                <th style={thStyle}>👤 Name</th>
                <th style={thStyle}>📧 Email</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((survey) => (
                <tr key={survey.id}>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    {survey.speaker_id}
                  </td>
                  <td style={{ ...tdStyle, color: "#1e293b" }}>
                    {survey.userName}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      color: "#64748b",
                      fontSize: "13px",
                    }}
                  >
                    {survey.userEmail}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleViewClick(survey)}
                      onMouseEnter={(e) =>
                        Object.assign(e.target.style, btnHoverStyle)
                      }
                      onMouseLeave={(e) =>
                        Object.assign(e.target.style, btnStyle)
                      }
                      style={btnStyle}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPopup && selectedSurvey && (
        <div style={modalOverlayStyle} onClick={closePopup}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "0px 32px",
                background:
                  "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                borderBottom: "1px solid #e2e8f0",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h3
                    style={{
                      fontSize: "28px",
                      fontWeight: "800",
                      background:
                        "linear-gradient(135deg, #9b1321c7 0%, #9B1321 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      margin: "0 0 8px 0",
                    }}
                  >
                    Detailed Survey Responses
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "14px",
                    }}
                  >
                    <strong>{selectedSurvey.userName}</strong> | Speaker/Attendance
                    ID: {selectedSurvey.speaker_id} |{" "}
                    {new Date(
                      selectedSurvey.created_at
                    ).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={closePopup}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "28px",
                    cursor: "pointer",
                    color: "#94a3b8",
                    padding: "8px",
                    borderRadius: "50%",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
                >
                  ×
                </button>
              </div>
            </div>

            <div
              style={{
                padding: "32px",
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {Object.entries(selectedSurvey.answers).map(
                ([questionKey, answer]) => (
                  <div
                    key={questionKey}
                    style={{
                      marginBottom: "24px",
                      padding: "20px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      borderLeft: `4px solid ${getQuestionColor(
                        questionKey
                      )}`,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1e293b",
                        margin: "0 0 12px 0",
                        lineHeight: "1.4",
                      }}
                    >
                      {questions[questionKey]}
                    </h4>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        minHeight: "60px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "15px",
                          color: "#374151",
                          lineHeight: "1.6",
                          fontWeight:
                            (answer || "").length > 50 ? "normal" : "500",
                        }}
                      >
                        {answer || "No response"}
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>

            <div
              style={{
                padding: "24px 32px",
                backgroundColor: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                onClick={closePopup}
                style={{
                  ...btnStyle,
                  width: "100%",
                  padding: "14px",
                  fontSize: "16px",
                  background:
                    "linear-gradient(135deg, #9b1321 0%, #9b1321 100%)",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for question color coding
function getQuestionColor(questionKey) {
  const colors = {
    q1_satisfaction: "#3b82f6",
    q2_clarity: "#10b981",
    q3_technical_support: "#f59e0b",
    q4_organization: "#8b5cf6",
    q5_staff_support: "#ef4444",
    q6_schedule_management: "#06b6d4",
    q7_like_experience: "#eab308",
    q8_improvement: "#ec4899",
    q9_future_participation: "#14b8a6",
    q10_final_comments: "#6366f1",
  };
  return colors[questionKey] || "#6b7280";
}
